import { useState, type FormEvent } from 'react';
import { Button, Eyebrow } from '@sica/ui';
import { CodeInput } from '@/components/gate/CodeInput';
import type { SiteAccessError, SiteAccessOutcome } from '@/hooks/useSiteAccess';

export const CODE_LENGTH = 5;

const ERROR_COPY: Record<SiteAccessError, string> = {
  rejected: 'El código no es correcto. Revisalo y probá de nuevo.',
  unavailable: 'No pudimos verificar el código. Probá de nuevo en unos segundos.',
};

interface AccessCodeScreenProps {
  error: SiteAccessError | null;
  submitting: boolean;
  onSubmit: (code: string) => Promise<SiteAccessOutcome>;
}

/**
 * Pantalla de acceso: lo único que se ve del sitio hasta que el backend valida
 * el código. Presentacional — no sabe si el código es correcto, sólo lo manda
 * y muestra lo que le contestan.
 */
export function AccessCodeScreen({ error, submitting, onSubmit }: AccessCodeScreenProps) {
  const [code, setCode] = useState('');
  const complete = code.length === CODE_LENGTH;

  async function attempt(candidate: string): Promise<void> {
    // Código equivocado: se vacían las casillas y el foco vuelve solo a la
    // primera (ver CodeInput). Un fallo de red no las toca — ahí el código
    // puede estar bien y reescribirlo sería castigar al visitante.
    if ((await onSubmit(candidate)) === 'rejected') setCode('');
  }

  // El envío sale de la escritura, no de un efecto sobre el estado: así el
  // intento ocurre una vez por acción del visitante y nunca en un bucle.
  function handleChange(next: string): void {
    setCode(next);
    if (next.length === CODE_LENGTH) void attempt(next);
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    if (complete && !submitting) void attempt(code);
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-paper px-4 py-16">
      <div className="w-full max-w-prose text-center">
        <Eyebrow className="mb-6">SICA · Acceso anticipado</Eyebrow>

        <h1 className="font-display text-4xl font-medium leading-[1.05] tracking-tight text-accent md:text-5xl">
          Este sitio todavía
          <br />
          <span className="font-normal italic">no es público.</span>
        </h1>

        <p className="mx-auto mt-6 max-w-md text-lg leading-relaxed text-ink-muted">
          Ingresá el código de acceso para ver el contenido.
        </p>

        <form onSubmit={handleSubmit} className="mt-10">
          <CodeInput
            length={CODE_LENGTH}
            value={code}
            invalid={error === 'rejected'}
            disabled={submitting}
            describedBy={error ? 'access-code-error' : 'access-code-hint'}
            onChange={handleChange}
          />

          <p id="access-code-hint" className="mt-4 text-sm text-ink-subtle" hidden={Boolean(error)}>
            {CODE_LENGTH} caracteres. No distingue mayúsculas de minúsculas.
          </p>

          {error && (
            <p
              id="access-code-error"
              role="alert"
              className="mt-4 text-sm font-medium text-feedback-danger-fg"
            >
              {ERROR_COPY[error]}
            </p>
          )}

          <Button
            type="submit"
            variant="institutional"
            size="xl"
            disabled={!complete || submitting}
            className="mt-8 w-full sm:w-auto"
          >
            {submitting ? 'Verificando…' : 'Entrar'}
          </Button>
        </form>
      </div>
    </main>
  );
}
